import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Send, CheckCircle, Clock, Mail, Copy, Trash2 } from "lucide-react";

export default function AdminInvites() {
  const [invites, setInvites] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", course_id: "", group_id: "" });
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    const [i, c, g] = await Promise.all([api.get("/invites"), api.get("/courses"), api.get("/groups")]);
    setInvites(i.data);
    setCourses(c.data);
    setGroups(g.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openInvite = () => {
    setForm({ name: "", email: "", course_id: courses[0]?.id || "", group_id: "none", role: "student" });
    setDialogOpen(true);
  };

  const handleSend = async () => {
    if (!form.name || !form.email) {
      toast.error("Please fill in name and email");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        group_id: form.group_id === "none" ? null : form.group_id,
        course_id: (form.role === "student" || form.role === "instructor") ? form.course_id : null,
      };
      const { data } = await api.post("/invites", payload);
      toast.success(data.email_sent ? `Invitation sent to ${form.email}` : `Invitation created (email pending)`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (id) => {
    try {
      const { data } = await api.post(`/invites/${id}/resend`);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invitation for ${inv.name} (${inv.email})?`)) return;
    try {
      await api.delete(`/invites/${inv.id}`);
      toast.success("Invitation deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied to clipboard");
  };

  const getCourseName = (id) => courses.find((c) => c.id === id)?.title || "Unknown";
  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || "";
  const filteredGroups = form.course_id ? groups.filter((g) => g.course_id === form.course_id) : groups;

  const filteredInvites = filterStatus === "all" ? invites : invites.filter((i) => i.status === filterStatus);
  const pendingCount = invites.filter((i) => i.status === "pending").length;
  const acceptedCount = invites.filter((i) => i.status === "accepted").length;

  return (
    <AdminLayout>
      <div data-testid="admin-invites" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Invitations</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">
              {pendingCount} pending · {acceptedCount} accepted · {invites.length} total
            </p>
          </div>
          <Button data-testid="invite-student-button" onClick={openInvite} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> Invite Student
          </Button>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          {[
            { value: "all", label: "All" },
            { value: "pending", label: `Pending (${pendingCount})` },
            { value: "accepted", label: `Accepted (${acceptedCount})` },
          ].map((f) => (
            <button
              key={f.value}
              data-testid={`filter-invite-${f.value}`}
              onClick={() => setFilterStatus(f.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterStatus === f.value
                  ? "bg-[#5C3A2E] text-white"
                  : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Invite List */}
        <div className="grid gap-3">
          {filteredInvites.map((inv) => (
            <div key={inv.id} data-testid={`invite-card-${inv.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-4 sm:p-5 studio-card">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${inv.status === "accepted" ? "bg-[#5C3A2E]" : "bg-[#EADBC8]"}`}>
                    {inv.status === "accepted" ? <CheckCircle className="w-5 h-5 text-white" /> : <Clock className="w-5 h-5 text-[#5C3A2E]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-[#2B2B2B] text-sm">{inv.name}</h4>
                    <p className="text-xs text-[#2B2B2B]/40">{inv.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#C6A969]">{getCourseName(inv.course_id)}</span>
                      {inv.group_id && <span className="text-xs text-[#2B2B2B]/30">{getGroupName(inv.group_id)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${inv.status === "accepted" ? "status-completed" : "status-starting-soon"}`}>
                    {inv.status === "accepted" ? "Accepted" : "Pending"}
                  </span>
                  {inv.status === "pending" && (
                    <>
                      <Button data-testid={`resend-invite-${inv.id}`} variant="ghost" size="sm" onClick={() => handleResend(inv.id)} title="Resend" className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyLink(inv.invite_link)} title="Copy Link" className="text-[#C6A969] hover:bg-[#C6A969]/10 rounded-full h-8 w-8 p-0">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  <Button data-testid={`delete-invite-${inv.id}`} variant="ghost" size="sm" onClick={() => handleDelete(inv)} title="Delete" className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {filteredInvites.length === 0 && (
            <div className="text-center py-12">
              <Mail className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">
                {filterStatus === "all" ? "No invitations sent yet. Invite your first student." : `No ${filterStatus} invitations.`}
              </p>
            </div>
          )}
        </div>

        {/* Invite Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Invite User</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Send an invitation via email</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                <Input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name (e.g., Father of Adele)" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Email</label>
                <Input data-testid="invite-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="guardian">Guardian (Parent)</SelectItem>
                    <SelectItem value="spectator">Spectator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.role === "student" || form.role === "instructor") && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                    <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v, group_id: "none" })}>
                      <SelectTrigger data-testid="invite-course-select" className="rounded-xl border-[#EADBC8]">
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Group (optional)</label>
                    <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                      <SelectTrigger data-testid="invite-group-select" className="rounded-xl border-[#EADBC8]">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Assign later</SelectItem>
                        {filteredGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <Button data-testid="send-invite-button" onClick={handleSend} disabled={sending} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                <Mail className="w-4 h-4 mr-1.5" /> {sending ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
